import { useEffect } from "react";
import { UseInitializeProductsArgs } from "~/hooks/useInitializeProducts";
import useOpportunityProducts from "~/hooks/useOpportunityProducts";
import useSkinProducts from "~/hooks/useSkinProducts";
import useTreatmentType from "~/hooks/useTreatmentType";
import { SKIN_FAMILIES } from "~/stores/checkout/product.constants";
import { useCheckoutStore } from "~/stores/checkout/useCheckoutStore";
import useConfigurationSectionStore from "~/stores/headConfiguration";
import { SkinSection } from "~/stores/headConfiguration/ConfigurationSection.types";

type UseInitializeSkinProductsArgs = UseInitializeProductsArgs;

const useInitializeSkinProducts = ({
  opportunity,
  loadingOpportunity,
}: UseInitializeSkinProductsArgs) => {
  const { skinSelected, skinCategory, toggleProductCategory } =
    useTreatmentType();
  const [setSkinSections, skinSections] = useConfigurationSectionStore(
    (state) => [state.setSkinSections, state.skinSections]
  );
  const [selectedProducts, setSelectedProducts, setHasSkinCare] =
    useCheckoutStore((state) => [
      state.selectedProducts,
      state.setSelectedProducts,
      state.setHasSkinCare,
    ]);
  const { skinOpportunityProducts, isLoading: isOpportunityProductsLoading } =
    useOpportunityProducts(opportunity);
  const { hasSkinProducts, removeSkinProducts, isSkinBundlesLoading } =
    useSkinProducts();

  useEffect(() => {
    if (!loadingOpportunity) {
      // Auto-select skin category if there are opportunity skin products
      if (skinOpportunityProducts.length > 0 && !skinSelected && skinCategory) {
        toggleProductCategory(skinCategory);
      }
    }
  }, [
    loadingOpportunity,
    skinOpportunityProducts,
    skinSelected,
    skinCategory,
    toggleProductCategory,
  ]);

  useEffect(() => {
    if (!loadingOpportunity && !skinSelected && hasSkinProducts) {
      // Don't remove opportunity skin products
      const hasNonOpportunitySkinProducts = selectedProducts.some(
        (product) =>
          SKIN_FAMILIES.includes(product.family) &&
          !opportunity?.products.includes(product.id)
      );

      if (hasNonOpportunitySkinProducts) {
        removeSkinProducts();
      }
    }
  }, [loadingOpportunity, skinSelected, hasSkinProducts]);

  // only run when page is first loaded and no skin products are selected
  // => for a new tab
  useEffect(() => {
    if (
      !loadingOpportunity &&
      !isOpportunityProductsLoading &&
      !isSkinBundlesLoading
    ) {
      if (
        opportunity &&
        opportunity.skinConfiguration.length > 0 &&
        skinSections.length === 0
      ) {
        setSkinSections(opportunity.skinConfiguration as SkinSection[]);
      }

      // Add opportunity skin products if they're not already in selectedProducts
      if (skinOpportunityProducts.length > 0) {
        const missingOpportunitySkinProducts = skinOpportunityProducts.filter(
          (opportunityProduct) =>
            !selectedProducts.some(
              (selectedProduct) => selectedProduct.id === opportunityProduct.id
            )
        );

        if (missingOpportunitySkinProducts.length > 0) {
          const opportunitySkinCare = opportunity?.hasSkinCare();
          const hasSkinCare =
            opportunitySkinCare !== undefined ? opportunitySkinCare : true;
          setHasSkinCare(hasSkinCare);

          const result = [
            ...selectedProducts,
            ...missingOpportunitySkinProducts,
          ];
          setSelectedProducts(result);
        }
      }
    }
  }, [
    loadingOpportunity,
    isOpportunityProductsLoading,
    isSkinBundlesLoading,
    skinOpportunityProducts,
    selectedProducts,
    opportunity,
    skinSections.length,
    setSkinSections,
    setHasSkinCare,
    setSelectedProducts,
  ]);
};

export default useInitializeSkinProducts;
